import { useEffect, useState, useContext, useCallback, useRef } from 'react';
import Pagination from '@/components/Common/Pagination.tsx';
import { RichSelect } from '@/components/Controls/RichSelect';
import { PageRequest, Processing } from '@/api/entities/processing.ts';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import {
  useGetNextDataReport,
  useGetAllProcessing,
  useGetAllComponentKey,
  useGetAllDefectKey,
  useDeleteFirestoreImageByImageUrl,
  useDeleteProcessing,
} from '@/hooks/ai.hooks';
import { mapComponentOptions } from '@/utils/ia.utils';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import Card from '@/components/Card';
import { TableTitle } from '@/components/Header/TableTitle';
import { TableHead } from '@/components/Datatable/TableHead';
import { StatusTag } from '@/components/Tag/StatusTag';
import Loader from '@/components/Loading/Loader';
import { TableCell } from '@/components/Datatable/TableCell';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { CellContentWithRedirect } from '@/components/Custom/Datatable/CellContentWithRedirect';
import { NoTableDataFound } from '@/components/Custom/NotFound/NoTableDataFound';
import { useLocalStorageState } from '@/hooks/local-storage.hooks';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { EngineCorrectionUploadModal } from '@/components/Custom/Modal/EngineCorrectionUploadModal';
import { showModal } from '@/utils/modal.utils';
import {
  AdvancedFilterPanel,
  AdvancedFilterToggle,
  FilterDateRange,
  FilterField,
  FilterGrid,
  ToneIcon,
  withoutEmptyOption,
} from '@/components/Custom/Filter/FilterKit';
import { ADMINISTRATION_TIPS } from '@/docs/tips/administration';
import {
  IconCar,
  IconCarCrash,
  IconCircleCheck,
  IconClock,
  IconPhotoSearch,
  IconPhotoUp,
  IconRobot,
  IconScan,
  IconShieldCheck,
  IconUpload,
  IconUserCheck,
} from '@tabler/icons-react';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { ImagePreviewModal } from '@/components/Custom/Modal/ImagePreviewModal';
import { twMerge } from 'tailwind-merge';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { useListAllUsers } from '@/hooks/user.hooks';
import { RoleName } from '@/api/entities/user-role-type';
import dayjs from 'dayjs';

/** The API uses 'all' for "no filter" while RichSelect uses ''. */
const toSelectValue = (value?: string) =>
  !value || value === 'all' ? '' : value;
const toFilterValue = (value: string) => value || 'all';

const PILL = {
  green: 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
  red: 'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
  purple: 'bg-purple-DF00FF/5 border-purple-DF00FF/20 text-purple-DF00FF/75',
};

const initialPageRequest: PageRequest = {
  page: 1,
  limit: 100,
  moderatorUserId: 'all',
  detectionStatus: 'all',
  status: 'all',
  verificationStatus: 'all',
  isManualUpload: 'all',
  key: '',
  defectKey: '',
};

export const EngineCorrectionListPage = () => {
  const { t } = useTranslation('engineCorrection');
  const { loggedInUser } = useAuth();
  const { isAdmin } = useUserRole();
  const { currentPage, setCurrentPage } = useContext(EngineCorrectionContext);
  initialPageRequest.page = currentPage;
  const ID_INIT = '64caef2d9f0e53ad5c5f2c13';
  const [filter, setFilter] = useState<PageRequest>(initialPageRequest);
  const [usedStartDate, setUsedStartDate] = useLocalStorageState({
    key: 'engineCorrection_usedStartDate',
    defaultValue: null,
  });
  const [usedEndDate, setUsedEndDate] = useLocalStorageState({
    key: 'engineCorrection_usedEndDate',
    defaultValue: null,
  });
  const [moderatorUserId, setModeratorUserId] = useLocalStorageState({
    key: 'engineCorrection_moderatorUserId',
    defaultValue: 'all',
  });
  const [detectionStatus, setDetectionStatus] = useLocalStorageState({
    key: 'engineCorrection_detectionStatus',
    defaultValue: 'all',
  });
  const [moderationStatus, setModerationStatus] = useLocalStorageState({
    key: 'engineCorrection_moderationStatus',
    defaultValue: 'all',
  });
  const [verificationStatus, setVerificationStatus] = useLocalStorageState({
    key: 'engineCorrection_verificationStatus',
    defaultValue: 'all',
  });
  const [isManualUpload, setIsManualUpload] = useLocalStorageState({
    key: 'engineCorrection_uploadedImagesStatus',
    defaultValue: 'all',
  });
  const [componentKey, setComponentKey] = useLocalStorageState({
    key: 'engineCorrection_componentKey',
  });
  const [defectKey, setDefectKey] = useLocalStorageState({
    key: 'engineCorrection_defectKey',
  });
  const filtersEmptyRef = useRef<boolean>(true);
  const { data, isLoading, isFetching } = useGetAllProcessing(filter);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [processings, setProcessings] = useState<Processing[]>([]);
  const [selectedProcessing, setSelectedProcessing] = useState<Processing>();
  const [isUploadingImages, setIsUploadingImages] = useState<boolean>(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const { data: allModerators, isLoading: isLoadingAllModerators } =
    useListAllUsers({
      roleTypeNames: [RoleName.ADMIN, RoleName.MODERATOR],
    });

  let allModeratorsOptions: { value: string; label: string }[] = [];

  if (isAdmin) {
    allModeratorsOptions =
      allModerators?.data?.map((moderator) => ({
        value: moderator?.id || '',
        label: `${moderator?.firstname || ''} ${
          moderator?.lastname?.toUpperCase() || ''
        }`,
      })) ?? [];
  } else {
    allModeratorsOptions =
      allModerators?.data
        ?.filter((moderator) => moderator?.id === loggedInUser?.id)
        .map((moderator) => ({
          value: moderator?.id || '',
          label: t('common:myself'),
        })) ?? [];
  }

  const { data: dataToModerate } = useGetNextDataReport(ID_INIT);
  const { data: keyCars, isLoading: isLoadingKeyCars } =
    useGetAllComponentKey();
  const { data: defectKeyCars, isLoading: isLoadingDefectKeyCars } =
    useGetAllDefectKey();

  const { mutate: deleteProcessing, isPending: isPendingDeleteProcessing } =
    useDeleteProcessing({
      t,
      handleSuccess: () => {
        deleteFirestoreImage(selectedProcessing?.imageUrl);
      },
    });

  const {
    mutate: deleteFirestoreImage,
    isPending: isPendingDeleteFirestoreImage,
  } = useDeleteFirestoreImageByImageUrl({
    t,
    handleSuccess: () => {
      setSelectedProcessing(undefined);
    },
  });

  useEffect(() => {
    // Clear the current data immediately when filters change to prevent showing stale data
    setProcessings([]);
    setTotalPages(0);

    // Reset page to 1 when filters change
    setCurrentPage(1);

    setFilter((prevFilter) => ({
      ...prevFilter,
      page: 1, // Always reset to page 1 when filters change
      usedStartDate: usedStartDate ?? undefined,
      usedEndDate: usedEndDate ?? undefined,
      moderatorUserId: moderatorUserId,
      detectionStatus: detectionStatus,
      status: moderationStatus,
      verificationStatus: verificationStatus,
      isManualUpload: isManualUpload,
      key: componentKey,
      defectKey: defectKey,
    }));

    filtersEmptyRef.current =
      moderatorUserId === 'all' &&
      detectionStatus === 'all' &&
      moderationStatus === 'all' &&
      verificationStatus === 'all' &&
      isManualUpload === 'all' &&
      !componentKey &&
      !defectKey &&
      !usedStartDate &&
      !usedEndDate;
  }, [
    moderatorUserId,
    detectionStatus,
    moderationStatus,
    verificationStatus,
    isManualUpload,
    componentKey,
    defectKey,
    usedStartDate,
    usedEndDate,
    setCurrentPage,
  ]);

  const isLoadingAll =
    isLoading ||
    isFetching ||
    isLoadingKeyCars ||
    isLoadingDefectKeyCars ||
    isLoadingAllModerators ||
    isUploadingImages ||
    isPendingDeleteProcessing ||
    isPendingDeleteFirestoreImage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilter({
      ...filter,
      page,
    });
  };

  useEffect(() => {
    setProcessings(data?.data?.data ?? []);
    setTotalPages(data?.data?.totalPages ?? 0);
  }, [data]);

  const searchUsedStartDate = (value: dayjs.Dayjs) => {
    setUsedStartDate(value.format('YYYY-MM-DD'));
  };

  const searchUsedEndDate = (value: dayjs.Dayjs) => {
    setUsedEndDate(value.format('YYYY-MM-DD'));
  };

  const filterByModeratorUserId = (value: string) => {
    setModeratorUserId(value);
  };

  const filterByDetectionStatus = (detectionStatusValue: string) => {
    setDetectionStatus(detectionStatusValue);
  };

  const filterByModerationStatus = (value: string) => {
    setModerationStatus(value);
  };

  const filterByVerificationStatus = (verificationStatusValue: string) => {
    setVerificationStatus(verificationStatusValue);
  };

  const filterByUploadedImagesStatus = (uploadedImagesStatusValue: string) => {
    setIsManualUpload(uploadedImagesStatusValue);
  };

  const filterByLimit = (limit: number) => {
    setCurrentPage(1);
    setFilter({
      ...filter,
      page: 1,
      limit,
    });
  };

  const advancedFiltersCount = [
    usedStartDate || usedEndDate,
    moderatorUserId !== 'all',
    isManualUpload !== 'all',
    detectionStatus !== 'all',
    moderationStatus !== 'all',
    verificationStatus !== 'all',
  ].filter(Boolean).length;

  const filterByComponentKey = (componentKeyValue: string) => {
    setComponentKey(componentKeyValue);
  };

  const filterByDefectKey = (defectKeyValue: string) => {
    setDefectKey(defectKeyValue);
  };

  const clearFilters = () => {
    setUsedStartDate(null);
    setUsedEndDate(null);
    setModeratorUserId('all');
    setDetectionStatus('all');
    setModerationStatus('all');
    setVerificationStatus('all');
    setIsManualUpload('all');
    setComponentKey('');
    setDefectKey('');
    setCurrentPage(1);
  };

  const onClickToModerate = useCallback(() => {
    window.location.href = `/engine-correction/${dataToModerate?.data.id}`;
  }, [dataToModerate]);

  const onUploadingImages = (state: boolean) => {
    setIsUploadingImages(state);
  };

  const handleDeleteImage = () => {
    deleteProcessing(selectedProcessing?.id);
  };

  return (
    <>
      <PageHeader
        title={t('engineCorrectionElements')}
        totalItems={data?.data?.total}
        headingElement={
          <PageInfoHeading text={t('common:detailsRelatedToCurrentSection')} />
        }
        actionListClassName="max-lg:w-full max-lg:justify-center [&>*]:max-lg:w-full [&>*]:max-lg:h-auto [&>*]:max-lg:py-2.5"
        actionListElement={
          <>
            <HeadingButton
              buttonClassName="border-purple-5433FF/45 bg-white"
              textClassName="text-primary"
              text={t('common:uploadImages')}
              leftIcon={
                <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-primary" />
              }
              disabled={isLoadingAll}
              onClick={() => {
                showModal('#engine-correction-upload-modal');
              }}
            />
            <HeadingButton
              buttonClassName="border-purple-5433FF/45 bg-blue-50"
              textClassName="text-primary"
              text={t('startModeration')}
              leftIcon={
                <i className="-mt-[1.5px] ki-outline ki-size !text-[18px] text-primary font-bold" />
              }
              disabled={isLoadingAll || !dataToModerate?.data?.id}
              onClick={onClickToModerate}
            />
          </>
        }
      />
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle
              textClassName="mt-1.5"
              text={t('elementList')}
              infoTip={ADMINISTRATION_TIPS.engineCorrectionTable}
            />
            <div className="hidden max-md:block">
              <ClearButton
                disabled={filtersEmptyRef.current}
                onClick={clearFilters}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3.5 ml-auto max-1.5xl:ml-0 max-1.5xl:w-full">
            <div className="flex flex-wrap items-center flex-1 gap-3.5 max-1.5xl:basis-full">
              <RichSelect
                searchable
                containerClassName="w-[280px] max-1.5xl:min-w-0 max-sm:basis-full max-sm:w-full"
                leftIcon={<ToneIcon icon={IconCar} tone="sky" size="sm" />}
                placeholder={t('common:allEquipments')}
                aria-label={t('common:equipment')}
                loading={isLoadingKeyCars}
                options={withoutEmptyOption(
                  mapComponentOptions(keyCars?.data, '1', t),
                )}
                value={componentKey ?? ''}
                onValueChange={filterByComponentKey}
              />
              <RichSelect
                searchable
                containerClassName="w-[280px] max-1.5xl:min-w-0 max-sm:basis-full max-sm:w-full"
                leftIcon={
                  <ToneIcon icon={IconCarCrash} tone="rose" size="sm" />
                }
                placeholder={t('common:allDamages')}
                aria-label={t('common:damage')}
                loading={isLoadingDefectKeyCars}
                options={withoutEmptyOption(
                  mapComponentOptions(defectKeyCars?.data, '2', t),
                )}
                value={defectKey ?? ''}
                onValueChange={filterByDefectKey}
              />
              <div className="hidden max-1.5xl:block max-md:hidden">
                <ClearButton
                  disabled={filtersEmptyRef.current}
                  onClick={clearFilters}
                />
              </div>
            </div>
            <div className="flex justify-end max-1.5xl:w-full">
              <AdvancedFilterToggle
                open={showAdvancedFilters}
                activeCount={advancedFiltersCount}
                onToggle={() => setShowAdvancedFilters((prev) => !prev)}
              />
            </div>
            <div className="max-1.5xl:hidden">
              <ClearButton
                disabled={filtersEmptyRef.current}
                onClick={clearFilters}
              />
            </div>
          </div>
        </div>
        <AdvancedFilterPanel open={showAdvancedFilters}>
          <FilterGrid>
            <FilterDateRange
              label={t('common:modifyBetween')}
              startDate={usedStartDate ?? undefined}
              endDate={usedEndDate ?? undefined}
              onStartChange={searchUsedStartDate}
              onEndChange={searchUsedEndDate}
              className="sm:col-span-2"
            />
            <FilterField
              icon={IconUserCheck}
              tone="violet"
              label={t('common:moderator')}
            >
              <RichSelect
                searchable
                placeholder={t('common:allModerators')}
                options={allModeratorsOptions}
                value={toSelectValue(moderatorUserId)}
                onValueChange={(value) =>
                  filterByModeratorUserId(toFilterValue(value))
                }
              />
            </FilterField>
            <FilterField
              icon={IconPhotoUp}
              tone="indigo"
              label={t('imageOrigin_plural')}
            >
              <RichSelect
                placeholder={t('allOrigins')}
                options={[
                  {
                    label: t('uploaded_feminine'),
                    value: 'true',
                    pillClassName: PILL.green,
                    icon: <IconUpload size={12} stroke={2.4} />,
                  },
                  {
                    label: t('automatedReceipt'),
                    value: 'false',
                    pillClassName: PILL.purple,
                    icon: <IconRobot size={12} stroke={2.4} />,
                  },
                ]}
                value={toSelectValue(isManualUpload)}
                onValueChange={(value) =>
                  filterByUploadedImagesStatus(toFilterValue(value))
                }
              />
            </FilterField>
            <FilterField
              icon={IconScan}
              tone="sky"
              label={t('common:detectionStatus_plural')}
            >
              <RichSelect
                placeholder={t('common:allStatuses')}
                options={[
                  {
                    label: t('common:withDetections'),
                    value: 'true',
                    icon: (
                      <IconPhotoSearch size={15} className="text-sky-600" />
                    ),
                  },
                  {
                    label: t('common:withoutDetections'),
                    value: 'false',
                    icon: (
                      <IconPhotoSearch size={15} className="text-gray-400" />
                    ),
                  },
                ]}
                value={toSelectValue(detectionStatus)}
                onValueChange={(value) =>
                  filterByDetectionStatus(toFilterValue(value))
                }
              />
            </FilterField>
            <FilterField
              icon={IconShieldCheck}
              tone="emerald"
              label={t('common:moderationStatus')}
            >
              <RichSelect
                placeholder={t('common:allStatuses')}
                options={[
                  {
                    label: t('common:toModerate'),
                    value: 'A_MODERER',
                    pillClassName: PILL.red,
                    icon: <IconClock size={12} stroke={2.4} />,
                  },
                  {
                    label: t('common:moderated_feminine'),
                    value: 'MODERE',
                    pillClassName: PILL.green,
                    icon: <IconCircleCheck size={12} stroke={2.4} />,
                  },
                ]}
                value={toSelectValue(moderationStatus)}
                onValueChange={(value) =>
                  filterByModerationStatus(toFilterValue(value))
                }
              />
            </FilterField>
            <FilterField
              icon={IconCircleCheck}
              tone="teal"
              label={t('common:verificationStatus')}
            >
              <RichSelect
                placeholder={t('common:allStatuses')}
                options={[
                  {
                    label: t('common:toVerify'),
                    value: 'TO_VERIFY',
                    pillClassName: PILL.red,
                    icon: <IconClock size={12} stroke={2.4} />,
                  },
                  {
                    label: t('common:verified_feminine'),
                    value: 'VERIFIED',
                    pillClassName: PILL.green,
                    icon: <IconCircleCheck size={12} stroke={2.4} />,
                  },
                ]}
                value={toSelectValue(verificationStatus)}
                onValueChange={(value) =>
                  filterByVerificationStatus(toFilterValue(value))
                }
              />
            </FilterField>
          </FilterGrid>
        </AdvancedFilterPanel>
        <div data-datatable="true" data-datatable-page-size="5">
          <div className="scrollable-x-auto isolate">
            <table
              className="table table-auto table-border"
              data-datatable-table="true"
              id="grid_table"
            >
              <thead>
                <tr>
                  <TableHead
                    text={t('common:image')}
                    thClassName="text-center min-w-[250px]"
                  />
                  <TableHead
                    text={t('common:nbElementsDetected')}
                    infoTip={ADMINISTRATION_TIPS.elementsDetectedColumn}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbDamagesDetected')}
                    infoTip={ADMINISTRATION_TIPS.damagesDetectedColumn}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbCorrectionsOfElements')}
                    infoTip={ADMINISTRATION_TIPS.elementCorrectionsColumn}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbCorrectionsOfDamages')}
                    infoTip={ADMINISTRATION_TIPS.damageCorrectionsColumn}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('imageOrigin')}
                    infoTip={ADMINISTRATION_TIPS.imageOriginColumn}
                    thClassName="min-w-[140px]"
                    sortable
                  />
                  <TableHead
                    text={t('common:moderation')}
                    infoTip={ADMINISTRATION_TIPS.moderationColumn}
                    thClassName="w-[130px]"
                    textClassName="text-center"
                    sortable
                  />
                  <TableHead thClassName="sticky-action-column" />
                </tr>
              </thead>
              {!isLoading && !isFetching && processings.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={7}>
                      <NoTableDataFound t={t} />
                    </td>
                  </tr>
                </tbody>
              ) : (
                processings.map((item: Processing) => {
                  const isManualUpload = item?.isManualUpload;

                  const isModerated =
                    item?.status === HumanModerationStatus.MODERE;

                  return (
                    <tbody key={item?.id}>
                      <tr className="border-b not-last:border-gray-E5E7EB dark-mode:border-gray-200">
                        <TableCell
                          content={
                            <img
                              className="h-full w-auto object-contain object-center rounded-md m-auto hover:cursor-pointer"
                              alt=""
                              src={item?.imageUrl}
                              onClick={() => {
                                setSelectedProcessing(item);
                                showModal('#engine-correction-preview-modal');
                              }}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.numElementsDetected || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.numDamagesDetected || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.correctedElements || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.correctedDamages || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          tdClassName="text-center"
                          content={
                            <StatusTag
                              text={`${
                                isManualUpload
                                  ? t('uploaded_feminine')
                                  : t('automatedReceipt')
                              }`}
                              className={twMerge(
                                'pt-[6px] pb-[5px] text-[10.1px] font-semibold [word-spacing:1.9px] leading-snug uppercase',
                                isManualUpload &&
                                  'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
                                !isManualUpload &&
                                  'bg-purple-DF00FF/5 border-purple-DF00FF/20 text-purple-DF00FF/75',
                              )}
                            />
                          }
                        />
                        <TableCell
                          tdClassName="!px-2.5 text-center"
                          content={
                            <>
                              <StatusTag
                                text={`${
                                  isModerated
                                    ? t('common:moderated_feminine')
                                    : t('common:toModerate')
                                }`}
                                className={twMerge(
                                  'pt-[6px] pb-[5px] text-[10.1px] font-semibold [word-spacing:1.9px] leading-snug uppercase',
                                  isModerated &&
                                    'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
                                  !isModerated &&
                                    'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
                                )}
                              />
                              {item?.status ===
                                HumanModerationStatus.MODERE && (
                                <div className="mt-2">
                                  <div
                                    className={twMerge(
                                      'text-[11px] text-primary font-semibold [word-spacing:1.9px] leading-snug uppercase',
                                      !item?.moderationVerifiedByUserId &&
                                        'text-red-FF3680',
                                    )}
                                  >
                                    -&nbsp;
                                    {item?.moderationVerifiedByUserId
                                      ? t('common:verified_feminine')
                                      : t('common:toVerify')}
                                    &nbsp;-
                                  </div>
                                </div>
                              )}
                            </>
                          }
                        />
                        <TableCell
                          tdClassName="sticky-action-column"
                          content={
                            <TableMenu disabled={isLoadingAll}>
                              {isModerated && (
                                // <SubMenuItem
                                //   text={t('alreadyValidated_feminine')}
                                //   itemClassName="hover:!bg-transparent"
                                //   leftIconClassName="mt-0.5 ki-information-2 !text-[19px]"
                                //   disabled
                                // />
                                <SubMenuItem
                                  text={t('common:moderate')}
                                  leftIconClassName="mt-0.5 ki-toggle-on !text-[17.26px]"
                                  onClick={() =>
                                    (window.location.href = `/engine-correction/${item.id}`)
                                  }
                                />
                              )}
                              {!isModerated && (
                                <SubMenuItem
                                  text={t('common:moderate')}
                                  leftIconClassName="mt-0.5 ki-toggle-on !text-[17.26px]"
                                  disabled={
                                    isModerated ||
                                    isPendingDeleteProcessing ||
                                    isPendingDeleteFirestoreImage
                                  }
                                  onClick={() =>
                                    (window.location.href = `/engine-correction/${item.id}`)
                                  }
                                />
                              )}
                              {isManualUpload && !isModerated && (
                                <>
                                  <SubMenuItem
                                    text={t('common:edit')}
                                    leftIconClassName="mt-0.5 ki-notepad-edit !text-[16.7px]"
                                    disabled={
                                      isModerated ||
                                      isPendingDeleteProcessing ||
                                      isPendingDeleteFirestoreImage
                                    }
                                    onClick={() => {
                                      setSelectedProcessing(item);
                                      showModal(
                                        '#engine-correction-upload-modal',
                                      );
                                    }}
                                  />
                                  <SubMenuItem
                                    text={t('common:delete')}
                                    leftIconClassName="ki-trash !text-[19px]"
                                    textClassName="line-clamp-1"
                                    disabled={
                                      isPendingDeleteProcessing ||
                                      isPendingDeleteFirestoreImage
                                    }
                                    onClick={() => {
                                      setSelectedProcessing(item);
                                      showModal(
                                        '#confirmation-remove-item-modal',
                                      );
                                    }}
                                  />
                                </>
                              )}
                            </TableMenu>
                          }
                        />
                      </tr>
                    </tbody>
                  );
                })
              )}
            </table>
          </div>
          <div className="mt-4">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              pageSize={filter.limit}
              onPageSizeChange={filterByLimit}
              totalItems={data?.data?.total}
            />
          </div>
        </div>
      </Card>
      <EngineCorrectionUploadModal
        t={t}
        keyCarsData={keyCars?.data}
        defectKeyCarsData={defectKeyCars?.data}
        selectedProcessing={selectedProcessing}
        setSelectedProcessing={setSelectedProcessing}
        onUploadingImages={onUploadingImages}
      />
      <ImagePreviewModal
        id={'engine-correction-preview-modal'}
        bodyClassName="h-max max-h-[93.5vh] min-[1920px]:h-auto"
        isOpen={selectedProcessing !== undefined}
        onClose={() => {
          setSelectedProcessing(undefined);
        }}
        onClickOutsideContent={() => {
          setSelectedProcessing(undefined);
        }}
        onEscapePress={() => {
          setSelectedProcessing(undefined);
        }}
      >
        <img
          className="max-h-screen w-auto object-contain object-center select-none [-webkit-touch-callout:none] [-webkit-user-select:none]"
          src={selectedProcessing?.imageUrl}
          alt=""
        />
      </ImagePreviewModal>
      <ConfirmationModal
        id="confirmation-remove-item-modal"
        title={t('common:confirmYourAction')}
        description={`${t('areYourSureToDeleteThisImage')} ?`}
        onConfirmLabel={t('common:delete')}
        onConfirm={() => {
          handleDeleteImage();
        }}
      />
      {isLoadingAll && <Loader />}
    </>
  );
};
